import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Alert,
    Chip,
    Breadcrumbs,
    Link as MuiLink
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import {
    scanGeneralConsignment,
    ScanConsignmentData
} from '../../services/consignmentApi';
import { ConsignmentView } from '../Consignments';

const ScanConsignments = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [scanInput, setScanInput] = useState('');
    const [scanFeedback, setScanFeedback] = useState('');
    const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
    const [scannedConsignments, setScannedConsignments] = useState<ScanConsignmentData[]>([]);
    const [includedIds, setIncludedIds] = useState<number[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedConsignmentId, setSelectedConsignmentId] = useState<number | null>(null);

    useEffect(() => {
        if (scanInput.length === 17) {
            handleScan();
        }
    }, [scanInput]);

    const handleScan = async () => {
        try {
            setScanFeedback('');
            const result = await scanGeneralConsignment({
                consignment_no: scanInput
            });

            if (result.success) {
                // Check for duplicates
                if (includedIds.includes(result.id)) {
                    setScanFeedback('Consignment Already added.');
                    setFeedbackType('error');
                } else {
                    setScannedConsignments([...scannedConsignments, result.data]);
                    setIncludedIds([...includedIds, result.id]);
                    setScanFeedback(result.message);
                    setFeedbackType('success');
                    setScanInput('');
                }
            }
        } catch (error: any) {
            setScanFeedback(error.message || 'Failed to scan consignment');
            setFeedbackType('error');
        }
    };

    const handleRemoveConsignment = (id: number) => {
        setScannedConsignments(scannedConsignments.filter(c => c.id !== id));
        setIncludedIds(includedIds.filter(cid => cid !== id));
    };

    const handleViewConsignment = (id: number) => {
        setSelectedConsignmentId(id);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedConsignmentId(null);
    };

    const getStatusColor = (status: string) => {
        const statusLower = status.toLowerCase();
        if (statusLower.includes('delivered')) return 'success';
        if (statusLower.includes('failed')) return 'error';
        if (statusLower.includes('pending') || statusLower.includes('booked')) return 'warning';
        return 'info';
    };

    return (
        <Box>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/consignments')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Consignments
                </MuiLink>
                <Typography color="text.primary">Scan</Typography>
            </Breadcrumbs>

            {/* Main Card */}
            <Card>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                        Scan Consignments
                    </Typography>

                    {/* Scan Section */}
                    <Box sx={{ my: 5, textAlign: 'center' }}>
                        <Typography variant="h6" gutterBottom>
                            Scan Consignments ({scannedConsignments.length})
                        </Typography>
                        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
                            <TextField
                                fullWidth
                                placeholder="Enter Consignment Number"
                                value={scanInput}
                                onChange={(e) => setScanInput(e.target.value)}
                                inputProps={{ maxLength: 17 }}
                                autoFocus
                                sx={{ mb: 2 }}
                            />
                            {scanFeedback && (
                                <Alert severity={feedbackType} sx={{ mb: 2 }}>
                                    {scanFeedback}
                                </Alert>
                            )}
                        </Box>
                    </Box>

                    {/* Scanned Consignments Table */}
                    {scannedConsignments.length > 0 && (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Consignment #</TableCell>
                                        <TableCell>Booking Type</TableCell>
                                        <TableCell>Service Type</TableCell>
                                        <TableCell>Booking Date</TableCell>
                                        <TableCell>Weight (Grams)</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scannedConsignments.map((consignment) => (
                                        <TableRow key={consignment.id}>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => handleViewConsignment(consignment.id)}
                                                    sx={{ textDecoration: 'none', cursor: 'pointer' }}
                                                >
                                                    {consignment.consignment_no}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{consignment.booking_type}</TableCell>
                                            <TableCell>{consignment.service_type}</TableCell>
                                            <TableCell>{consignment.created_at}</TableCell>
                                            <TableCell>{consignment.weight}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={consignment.status}
                                                    color={getStatusColor(consignment.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemoveConsignment(consignment.id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Consignment Details Modal */}
            <ConsignmentView
                open={modalOpen}
                onClose={handleCloseModal}
                consignmentId={selectedConsignmentId}
            />
        </Box>
    );
};

export default ScanConsignments;

